import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  CircularProgress,
  Box,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';

const formatStatus = (value = '') => (
  String(value || 'draft')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

const statusColor = (status = '') => {
  if (status === 'certified') {
    return 'success';
  }
  if (status === 'review_requested') {
    return 'warning';
  }
  if (status === 'archived') {
    return 'default';
  }
  return 'primary';
};

const TDPGallery = () => {
  const [tdps, setTdps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTDPs = async () => {
      try {
        const res = await axiosInstance.get('/tdps');
        setTdps(Array.isArray(res.data?.tdps) ? res.data.tdps : []);
        setError('');
      } catch (err) {
        console.error('Error fetching TDPs', err);
        setError(err?.response?.data?.message || 'Unable to load xTDP inventory right now.');
      } finally {
        setLoading(false);
      }
    };
    fetchTDPs();
  }, []);

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>Technical Data Packages</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Owned and shared xTDPs available for workspace exchange.
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? (
        <CircularProgress />
      ) : !tdps.length ? (
        <Alert severity="info">No xTDPs are available yet.</Alert>
      ) : (
        <Grid container spacing={3}>
          {tdps.map((tdp) => (
            <Grid item xs={12} sm={6} md={4} key={tdp.tdp_id}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardActionArea onClick={() => navigate(`/tdp/${tdp.tdp_id}`)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" color={statusColor(tdp.status)} label={formatStatus(tdp.status)} />
                        {tdp.source === 'shared' ? <Chip size="small" variant="outlined" label="Shared" /> : null}
                        {tdp.is_cui ? <Chip size="small" color="warning" variant="outlined" label="CUI" /> : null}
                      </Stack>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {tdp.name || tdp.tdp_title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Version {tdp.version || 'Unversioned'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Designed by: {tdp.designed_by || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated {tdp.updated_at ? new Date(tdp.updated_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, wordBreak: 'break-all' }}>
                      {tdp.tdp_id}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default TDPGallery;
